import { AuditDetails, Entity } from '../entity';

/**
 * The BIF Event types from the project specification. Manually-captured types are raised by a
 * user from the job/work order; auto-captured types are raised by the BGIS customisation handlers
 * when a domain trigger fires. The string values are the human-facing labels.
 */
export enum BifEventType {
    ContractorNotAnswering = 'Contractor Not Answering',
    ContractorRefusingJobs = 'Contractor Refusing Jobs',
    MilestoneMissedDueDate = 'Milestone Missed - Due Date',
    MilestoneMissedInProgress = 'Milestone Missed - In Progress',
    Timing = 'Timing',
    Quality = 'Quality',
    SafetyRelated = 'Safety Related',
    CodeOfConduct = 'Code of Conduct',
    ComplianceIssue = 'Compliance Issue',
    ClaimReturned = 'Claim Returned',
    ClaimRejected = 'Claim Rejected',
    StageReopened = 'Stage Reopened',
}

export enum BifEventSource {
    Manual = 'Manual',
    Auto = 'Auto',
}

/**
 * A BIF Event is a lightweight, immutable record of a contractor-performance deviation. Its primary
 * subject is the contractor; the job/work order association is optional (a BIF event may be raised
 * directly against a contractor with no related job). Reporting is keyed on contractor and raisedOn
 * (e.g. BIFs raised per quarter, most troublesome contractor).
 */
export interface BifEvent extends Entity, AuditDetails {
    eventType: BifEventType;
    source: BifEventSource;

    /** Primary subject — contractor performance. Optional to support future contractor-only events. */
    contractor?: Entity;

    /** Who the event is raised by/on behalf of. Defaults to the logged-in user but may be entered on behalf of someone else (distinct from the audit createdBy, which records the actual data-enterer). */
    raisedBy?: Entity;

    /** Optional job association. The job is the primary document; the work order (stage) is a subdocument of the job. */
    job?: Entity;
    workOrder?: Entity;

    /** When the BIF event was raised (manual: when the user raised it; auto: when the trigger fired). Primary reporting date. */
    raisedOn?: Date;

    /** Free-text details. Auto-capture handlers write a plain-language summary of what triggered the event. */
    note?: string;
}
